import { useEffect, useRef, useState } from "react";
import {
  motion,
  useScroll,
  useTransform,
  useMotionValue,
  useSpring,
} from "framer-motion";
import {
  ArrowRight,
  ArrowUpRight,
  Brain,
  LineChart,
  Code2,
  Database,
  TrendingUp,
  BarChart3,
  BookOpen,
  Zap,
  Cpu,
  Sparkles,
} from "lucide-react";
import styles from "./Homepage.module.css";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { XquantLogo } from "../assets/XquantLogo";

const navLinks = [
  { label: "Platform", target: "#platform" },
  { label: "Capabilities", target: "#capabilities" },
  { label: "Workflow", target: "#workflow" },
  { label: "Trading", target: "/trading" },
  { label: "Market Monitor", target: "/market-monitor" },
  { label: "Strategy Systematics", target: "/strategy-systematics" },
];

const heroMetrics = [
  { value: "70+", label: "Symbols" },
  { value: "5Y", label: "History" },
  { value: "24/7", label: "Agents" },
];

const platformPillars = [
  {
    icon: Brain,
    eyebrow: "01 / Intelligence",
    title: "Multi-agent orchestration",
    desc: "Specialized agents coordinate research, analysis, backtesting, and education — every workflow runs through an AI that knows the markets.",
  },
  {
    icon: Zap,
    eyebrow: "02 / Velocity",
    title: "Sub-millisecond backtests",
    desc: "Rust-native execution engine streams five years of tick-level data through your strategy in the time it takes to blink.",
  },
  {
    icon: LineChart,
    eyebrow: "03 / Visualization",
    title: "Studio-grade charts",
    desc: "TradingView-class price action, custom 2D analytics, and real-time 3D volatility surfaces rendered in your browser.",
  },
  {
    icon: Code2,
    eyebrow: "04 / Extensibility",
    title: "Sandboxed Python runtime",
    desc: "Full pandas, numpy, and quant stacks executed in an isolated container — bring your own models, ship in seconds.",
  },
  {
    icon: Database,
    eyebrow: "05 / Coverage",
    title: "Deep market data",
    desc: "Equities, ETFs, full options chains, derived Greeks and IV surfaces — a single API surface, decade-long history.",
  },
  {
    icon: BookOpen,
    eyebrow: "06 / Mastery",
    title: "Education on demand",
    desc: "An always-available tutor explains concepts, decomposes strategies, and walks you through the math at your pace.",
  },
];

const capabilities = [
  "Natural language strategies",
  "Multi-asset backtesting",
  "Options Greeks",
  "Volatility surfaces",
  "Drawdown analytics",
  "Correlation matrices",
  "Custom Python",
  "TradingView charts",
  "Risk attribution",
  "Live paper trading",
];

const workflow = [
  {
    step: "01",
    title: "Describe",
    desc: "Speak the strategy in plain English. Covered calls, mean-reversion, multi-leg spreads — the orchestrator decomposes intent.",
  },
  {
    step: "02",
    title: "Generate",
    desc: "Agents author the code, validate parameters, and surface risks before a single backtest runs.",
  },
  {
    step: "03",
    title: "Backtest",
    desc: "Run a decade of history in milliseconds. Drawdowns, Sharpe, exposure, attribution — all of it, instantly.",
  },
  {
    step: "04",
    title: "Iterate",
    desc: "Refine through conversation. Tune knobs, fork variations, compare runs side by side until the edge is unmistakable.",
  },
];

const useCases = [
  {
    icon: TrendingUp,
    tag: "Derivatives",
    title: "Options strategy lab",
    desc: "Spreads, straddles, iron condors, custom multi-leg structures — backed by real Greeks and volatility surfaces.",
  },
  {
    icon: BarChart3,
    tag: "Research",
    title: "Quant analytics suite",
    desc: "Statistical tooling, factor decomposition, correlation studies, and performance attribution across asset classes.",
  },
  {
    icon: Sparkles,
    tag: "Education",
    title: "Learn by doing",
    desc: "Conversational tutor pairs theory with live data — go from first principles to professional research, on the same canvas.",
  },
];

function useCursorSpotlight() {
  const x = useMotionValue(-500);
  const y = useMotionValue(-500);

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      x.set(e.clientX);
      y.set(e.clientY);
    };
    window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
  }, [x, y]);

  return { x, y };
}

function MagneticButton({
  children,
  onClick,
  variant = "primary",
}: {
  children: React.ReactNode;
  onClick?: () => void;
  variant?: "primary" | "ghost";
}): JSX.Element {
  const ref = useRef<HTMLButtonElement | null>(null);
  const xRaw = useMotionValue(0);
  const yRaw = useMotionValue(0);
  const x = useSpring(xRaw, { stiffness: 200, damping: 18 });
  const y = useSpring(yRaw, { stiffness: 200, damping: 18 });

  const handleMove = (e: React.MouseEvent<HTMLButtonElement>) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const offsetX = e.clientX - (rect.left + rect.width / 2);
    const offsetY = e.clientY - (rect.top + rect.height / 2);
    xRaw.set(offsetX * 0.25);
    yRaw.set(offsetY * 0.35);
  };

  const handleLeave = () => {
    xRaw.set(0);
    yRaw.set(0);
  };

  return (
    <motion.button
      ref={ref}
      onClick={onClick}
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
      style={{ x, y }}
      className={
        variant === "primary" ? styles.primaryCta : styles.secondaryCta
      }
    >
      {children}
    </motion.button>
  );
}

function SplitTitle({ text, delay = 0 }: { text: string; delay?: number }): JSX.Element {
  const words = text.split(" ");
  return (
    <span className={styles.splitTitle}>
      {words.map((word, i) => (
        <span className={styles.wordWrap} key={`${word}-${i}`}>
          <motion.span
            initial={{ y: "110%" }}
            animate={{ y: "0%" }}
            transition={{
              duration: 0.9,
              delay: delay + i * 0.06,
              ease: [0.22, 1, 0.36, 1],
            }}
            className={styles.wordInner}
          >
            {word}
          </motion.span>
        </span>
      ))}
    </span>
  );
}

export default function Homepage(): JSX.Element {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading, isLoggingOut, loginWithRedirect } = useAuth();
  const { x, y } = useCursorSpotlight();

  const containerRef = useRef<HTMLDivElement | null>(null);
  const { scrollYProgress } = useScroll({ target: containerRef });
  const progressBar = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);
  const spotlightBackground = useTransform(
    [x, y],
    ([cx, cy]: number[]) =>
      `radial-gradient(420px circle at ${cx}px ${cy}px, rgba(var(--fg-rgb), 0.08), transparent 60%)`,
  );

  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const launchApp = () => {
    if (isAuthenticated) navigate("/chat");
    else loginWithRedirect();
  };

  const goTrading = () => {
    if (isAuthenticated) navigate("/trading");
    else loginWithRedirect();
  };

  const goMarketMonitor = () => {
    if (isAuthenticated) navigate("/market-monitor");
    else loginWithRedirect();
  };

  const goStrategySystematics = () => {
    if (isAuthenticated) navigate("/strategy-systematics");
    else loginWithRedirect();
  };

  const handleNav = (target: string) => {
    if (target.startsWith("#")) {
      const el = document.querySelector(target);
      el?.scrollIntoView({ behavior: "smooth" });
      return;
    }
    if (target === "/trading") {
      goTrading();
      return;
    }
    if (target === "/market-monitor") {
      goMarketMonitor();
      return;
    }
    if (target === "/strategy-systematics") {
      goStrategySystematics();
      return;
    }
    navigate(target);
    window.scrollTo(0, 0);
  };

  if (isLoading || isLoggingOut) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingSpinner} />
        <p>{isLoggingOut ? "Signing out" : "Loading"}</p>
      </div>
    );
  }

  return (
    <div ref={containerRef} className={styles.pageWrapper}>
      <motion.div className={styles.scrollProgress} style={{ width: progressBar }} />

      <motion.div
        className={styles.spotlight}
        style={{ background: spotlightBackground }}
      />

      <div className={styles.gridBackdrop} aria-hidden />
      <div className={styles.noise} aria-hidden />

      <nav className={`${styles.nav} ${scrolled ? styles.navScrolled : ""}`}>
        <div className={styles.navContent}>
          <button className={styles.logoButton} onClick={() => navigate("/home")}>
            <XquantLogo size={28} className={styles.logoMark} />
            <span className={styles.logoWord}>xquant</span>
          </button>
          <div className={styles.navLinks}>
            {navLinks.map((l) => (
              <button
                key={l.label}
                onClick={() => handleNav(l.target)}
                className={styles.navLink}
              >
                {l.label}
              </button>
            ))}
          </div>
          <button className={styles.navCta} onClick={launchApp}>
            <span>{isAuthenticated ? "Console" : "Launch"}</span>
            <ArrowUpRight size={14} />
          </button>
        </div>
      </nav>

      <section className={styles.hero}>
        <div className={styles.heroInner}>
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className={styles.heroBadge}
          >
            <span className={styles.pulse} />
            <span>Live · AI-native quant research</span>
          </motion.div>

          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className={styles.heroKicker}
          >
            An LLM-Native Quant Workbench
          </motion.p>

          <h1 className={styles.heroTitle}>
            <SplitTitle text="Quantitative research," delay={0.05} />
            <SplitTitle text="rebuilt around" delay={0.25} />
            <span className={styles.heroAccent}>
              <SplitTitle text="autonomous agents." delay={0.45} />
            </span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7 }}
            className={styles.heroSubtitle}
          >
            xquant is a single workspace for designing, backtesting, and
            shipping trading strategies. Speak intent — the system writes the
            code, runs the math, and explains the result.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.85 }}
            className={styles.heroCtas}
          >
            <MagneticButton onClick={launchApp}>
              <span>{isAuthenticated ? "Open console" : "Start free"}</span>
              <ArrowRight size={16} />
            </MagneticButton>
            <MagneticButton variant="ghost" onClick={goTrading}>
              <TrendingUp size={16} />
              <span>Paper trade</span>
            </MagneticButton>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.1 }}
            className={styles.heroMetrics}
          >
            {heroMetrics.map((m) => (
              <div key={m.label} className={styles.heroMetric}>
                <div className={styles.heroMetricValue}>{m.value}</div>
                <div className={styles.heroMetricLabel}>{m.label}</div>
              </div>
            ))}
          </motion.div>
        </div>

        <motion.div
          className={styles.heroOrbit}
          initial={{ opacity: 0, scale: 0.92 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.4, delay: 0.6 }}
        >
          <div className={styles.orbitRing} />
          <div className={`${styles.orbitRing} ${styles.orbitRingMid}`} />
          <div className={`${styles.orbitRing} ${styles.orbitRingOuter}`} />
          <div className={styles.orbitCore}>
            <Cpu size={26} />
          </div>
        </motion.div>
      </section>

      <section id="platform" className={styles.section}>
        <SectionHeader
          eyebrow="Platform"
          title="A complete research stack, condensed."
          subtitle="Six pillars working in concert. No tool-stitching, no context switching."
        />
        <div className={styles.bentoGrid}>
          {platformPillars.map((pillar, i) => (
            <motion.div
              key={pillar.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ duration: 0.7, delay: i * 0.05, ease: [0.22, 1, 0.36, 1] }}
              className={styles.bentoCard}
            >
              <div className={styles.bentoCardInner}>
                <div className={styles.bentoEyebrow}>{pillar.eyebrow}</div>
                <div className={styles.bentoIcon}>
                  <pillar.icon size={20} />
                </div>
                <h3 className={styles.bentoTitle}>{pillar.title}</h3>
                <p className={styles.bentoDesc}>{pillar.desc}</p>
                <div className={styles.bentoArrow}>
                  <ArrowUpRight size={16} />
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      <section id="capabilities" className={styles.capabilitiesSection}>
        <div className={styles.capabilitiesGrid}>
          <div className={styles.capabilitiesIntro}>
            <SectionEyebrow text="Capabilities" />
            <h2 className={styles.sectionTitle}>
              Everything a quant team needs, in a single canvas.
            </h2>
            <p className={styles.sectionSubtitle}>
              From idea to execution — the whole loop is on one surface,
              instrumented end to end.
            </p>
          </div>
          <ul className={styles.capabilitiesList}>
            {capabilities.map((c, i) => (
              <motion.li
                key={c}
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.04 }}
                className={styles.capabilityItem}
              >
                <span className={styles.capabilityIndex}>
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className={styles.capabilityLabel}>{c}</span>
                <span className={styles.capabilityRule} />
              </motion.li>
            ))}
          </ul>
        </div>
      </section>

      <section id="workflow" className={styles.section}>
        <SectionHeader
          eyebrow="Workflow"
          title="From sentence to signal in four moves."
          subtitle="Conversation drives the entire research loop."
        />
        <div className={styles.workflowGrid}>
          {workflow.map((s, i) => (
            <motion.div
              key={s.step}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ duration: 0.6, delay: i * 0.1 }}
              className={styles.workflowCard}
            >
              <div className={styles.workflowStep}>{s.step}</div>
              <div className={styles.workflowBody}>
                <h3 className={styles.workflowTitle}>{s.title}</h3>
                <p className={styles.workflowDesc}>{s.desc}</p>
              </div>
              <div className={styles.workflowLine} />
            </motion.div>
          ))}
        </div>
      </section>

      <section className={styles.useCasesSection}>
        <SectionHeader
          eyebrow="Use cases"
          title="Built for traders, quants, and the curious."
          subtitle="One platform, many entry points."
        />
        <div className={styles.useCasesGrid}>
          {useCases.map((u, i) => (
            <motion.div
              key={u.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.7, delay: i * 0.1 }}
              className={styles.useCaseCard}
              whileHover={{ y: -6 }}
            >
              <div className={styles.useCaseHeader}>
                <span className={styles.useCaseTag}>{u.tag}</span>
                <u.icon size={20} />
              </div>
              <h3 className={styles.useCaseTitle}>{u.title}</h3>
              <p className={styles.useCaseDesc}>{u.desc}</p>
              <div className={styles.useCaseLink}>
                Explore <ArrowRight size={14} />
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      <section className={styles.ctaSection}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className={styles.ctaInner}
        >
          <SectionEyebrow text="Get started" centered />
          <h2 className={styles.ctaTitle}>
            The next decade of trading research
            <br />
            is being written in conversation.
          </h2>
          <p className={styles.ctaSubtitle}>
            Step into the workspace. The agents are already running.
          </p>
          <div className={styles.ctaButtons}>
            <MagneticButton onClick={launchApp}>
              <span>{isAuthenticated ? "Open console" : "Start free"}</span>
              <ArrowRight size={16} />
            </MagneticButton>
          </div>
        </motion.div>
      </section>

      <footer className={styles.footer}>
        <div className={styles.footerTop}>
          <div className={styles.footerBrand}>
            <XquantLogo size={28} className={styles.logoMark} />
            <span className={styles.logoWord}>xquant</span>
          </div>
          <p className={styles.footerTagline}>
            AI-native quant research, built for the next generation of traders.
          </p>
        </div>
        <div className={styles.footerLinks}>
          <FooterColumn
            title="Product"
            items={[
              { label: "Platform", onClick: () => handleNav("#platform") },
              { label: "Capabilities", onClick: () => handleNav("#capabilities") },
              { label: "Workflow", onClick: () => handleNav("#workflow") },
              { label: "Trading", onClick: goTrading },
              { label: "Market Monitor", onClick: goMarketMonitor },
              { label: "Strategy Systematics", onClick: goStrategySystematics },
            ]}
          />
          <FooterColumn
            title="Company"
            items={[
              { label: "About", onClick: () => navigate("/about") },
              { label: "Privacy", onClick: () => navigate("/privacy") },
              { label: "Terms", onClick: () => navigate("/terms") },
            ]}
          />
        </div>
        <div className={styles.footerBottom}>
          <span>© {new Date().getFullYear()} xquant</span>
          <span>All rights reserved.</span>
        </div>
      </footer>
    </div>
  );
}

function SectionHeader({
  eyebrow,
  title,
  subtitle,
}: {
  eyebrow: string;
  title: string;
  subtitle: string;
}): JSX.Element {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.6 }}
      transition={{ duration: 0.7 }}
      className={styles.sectionHeader}
    >
      <SectionEyebrow text={eyebrow} />
      <h2 className={styles.sectionTitle}>{title}</h2>
      <p className={styles.sectionSubtitle}>{subtitle}</p>
    </motion.div>
  );
}

function SectionEyebrow({
  text,
  centered = false,
}: {
  text: string;
  centered?: boolean;
}): JSX.Element {
  return (
    <div
      className={`${styles.eyebrow} ${centered ? styles.eyebrowCentered : ""}`}
    >
      <span className={styles.eyebrowDot} />
      <span>{text}</span>
    </div>
  );
}

function FooterColumn({
  title,
  items,
}: {
  title: string;
  items: { label: string; onClick: () => void }[];
}): JSX.Element {
  return (
    <div className={styles.footerColumn}>
      <h4>{title}</h4>
      {items.map((item) => (
        <button
          key={item.label}
          className={styles.footerLink}
          onClick={item.onClick}
        >
          {item.label}
        </button>
      ))}
    </div>
  );
}

